/**
 * Rules-management route (#118).
 *
 * Tabs: "Generic" / "Customer-spezifisch". Tab + customerId live in the
 * URL via TanStack Router `validateSearch` so a deep-link to a specific
 * customer's rule view is bookmarkable.
 *
 * The page is a thin shell over `RulesTable` + `RuleEditDialog`. Loading
 * the live row for the edit dialog happens lazily via `GET /rules` (the
 * list response carries enough metadata) - if we need the body we open
 * the edit dialog with a placeholder body and rely on the list refetch
 * after save. For the Sprint-2 closure we instead pass the click target's
 * id into the dialog and let the dialog fetch the version-current body
 * from the versions endpoint when the operator opens the "Versionen"-Tab.
 *
 * Practical Sprint-2 trade-off: instead of a per-rule GET we cache the
 * body returned by the most recent `POST/PUT` via TanStack Query - the
 * very first edit-click on a rule we have not touched yet will use the
 * versions endpoint's `current` row to populate `body`.
 */
import {
  type RuleDraft,
  type RuleListItem,
  type RuleScope,
  RuleScopeEnum,
} from '@invoice/shared/schemas';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { z } from 'zod';

import { AuthRequiredState } from '@/components/AuthRequiredState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CustomerLookupField } from '@/components/CustomerLookupField';
import { type ExistingRule, RuleEditDialog } from '@/components/RuleEditDialog';
import { RuleTestRunDialog } from '@/components/RuleTestRunDialog';
import { RulesTable } from '@/components/RulesTable';
import { SupplierLookupField } from '@/components/SupplierLookupField';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useCustomerList } from '@/lib/queries/customers';
import {
  fetchRuleVersions,
  ruleVersionsKey,
  useDeleteRule,
  useRulesList,
  useUpdateRule,
} from '@/lib/queries/rules';
import { cn } from '@/lib/utils';

export const RulesSearchSchema = z.object({
  tab: RuleScopeEnum.optional(),
  customerId: z.string().min(1).optional(),
  customerName: z.string().min(1).optional(),
  supplierName: z.string().min(1).optional(),
});
export type RulesSearch = z.infer<typeof RulesSearchSchema>;

export const Route = createFileRoute('/rules')({
  validateSearch: (search) => RulesSearchSchema.parse(search),
  component: RulesPage,
});

function RulesPage() {
  const { t } = useI18n();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  return (
    <RulesWorkspacePage
      search={search}
      copy={{
        sectionLabel: t('rules.sectionLabel'),
        pageTitle: t('rules.pageTitle'),
        pageDescription: t('rules.pageDescription'),
      }}
      onSearchChange={(updater, replace = false) => {
        void navigate({
          replace,
          search: (prev) => updater(prev as RulesSearch),
        });
      }}
    />
  );
}

interface RulesWorkspaceCopy {
  sectionLabel: string;
  pageTitle: string;
  pageDescription: string;
}

interface RulesWorkspacePageProps {
  search: RulesSearch;
  copy: RulesWorkspaceCopy;
  onSearchChange: (updater: (prev: RulesSearch) => RulesSearch, replace?: boolean) => void;
}

export function RulesWorkspacePage({ search, copy, onSearchChange }: RulesWorkspacePageProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const customersQuery = useCustomerList();

  const tab: RuleScope = search.tab ?? 'generic';
  const customerId = search.customerId;
  const customerName = search.customerName;
  const supplierName = search.supplierName;
  const visibleCustomers = customersQuery.data?.items ?? [];

  useEffect(() => {
    if (!customerId || customerName || visibleCustomers.length === 0) return;
    const currentCustomer = visibleCustomers.find((customer) => customer.id === customerId);
    if (!currentCustomer) return;
    onSearchChange(
      (prev) => ({
        ...(prev as RulesSearch),
        customerId: currentCustomer.id,
        customerName: currentCustomer.name,
      }),
      true,
    );
  }, [customerId, customerName, onSearchChange, visibleCustomers]);

  useEffect(() => {
    if (tab === 'customer' || !supplierName) return;
    onSearchChange((prev) => {
      const next = { ...(prev as RulesSearch) };
      next.supplierName = undefined;
      return next;
    }, true);
  }, [onSearchChange, supplierName, tab]);

  const rulesQuery = useRulesList({
    scope: tab,
    customerId,
    supplierName: tab === 'customer' ? supplierName : undefined,
  });

  const updateMutation = useUpdateRule();
  const deleteMutation = useDeleteRule();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ExistingRule | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [testRunDraft, setTestRunDraft] = useState<RuleDraft | null>(null);
  const [testRunLoadingId, setTestRunLoadingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RuleListItem | null>(null);

  const handleEdit = async (row: RuleListItem) => {
    setEditLoading(true);
    try {
      // Fetch the current body via the versions endpoint - the list endpoint
      // does NOT carry the body to keep the response small.
      const versions = await queryClient.fetchQuery({
        queryKey: ruleVersionsKey(row.id),
        queryFn: () => fetchRuleVersions(row.id),
      });
      const current = versions.current;
      setEditTarget({
        id: row.id,
        scope: row.scope,
        customerId: row.customerId,
        customerName: customerName ?? null,
        supplierName: row.supplierName,
        title: current.title,
        body: current.body,
        active: current.active,
        version: current.version,
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleActive = async (row: RuleListItem) => {
    await updateMutation.mutateAsync({ id: row.id, patch: { active: !row.active } });
  };

  const handleTestRun = async (row: RuleListItem) => {
    setTestRunLoadingId(row.id);
    try {
      const versions = await queryClient.fetchQuery({
        queryKey: ruleVersionsKey(row.id),
        queryFn: () => fetchRuleVersions(row.id),
      });
      setTestRunDraft({
        scope: row.scope,
        customerId: row.customerId ?? customerId ?? null,
        supplierName: row.supplierName ?? null,
        title: versions.current.title,
        body: versions.current.body,
      });
    } finally {
      setTestRunLoadingId(null);
    }
  };

  const handleDelete = async (row: RuleListItem) => {
    setDeleteTarget(row);
  };

  const togglePendingId =
    updateMutation.isPending && updateMutation.variables ? updateMutation.variables.id : null;
  const deletePendingId =
    deleteMutation.isPending && deleteMutation.variables ? deleteMutation.variables : null;

  const switchTab = (next: RuleScope) => {
    onSearchChange((prev) => {
      const base = prev as RulesSearch;
      return { ...base, tab: next };
    });
  };

  const setCustomerFilter = (next: { id: string; name: string } | null) => {
    onSearchChange((prev) => ({
      ...(prev as RulesSearch),
      customerId: next?.id,
      customerName: next?.name,
    }));
  };

  const setSupplierFilter = (next: { id: string; name: string } | null) => {
    onSearchChange((prev) => ({
      ...(prev as RulesSearch),
      supplierName: next?.name,
    }));
  };

  const items = rulesQuery.data ?? [];
  const isUnauthorized = rulesQuery.error instanceof ApiError && rulesQuery.error.status === 401;

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            {copy.sectionLabel}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            {copy.pageTitle}
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">{copy.pageDescription}</p>
        </div>
        <Button
          type="button"
          onClick={() => setCreateOpen(true)}
          aria-label={t('rules.create')}
          data-testid="rules-create-trigger"
        >
          <Plus className="size-4" aria-hidden />
          {t('rules.create')}
        </Button>
      </header>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2" role="tablist">
          <TabButton
            active={tab === 'generic'}
            onClick={() => switchTab('generic')}
            testId="rules-tab-generic"
          >
            {t('rules.tab.generic')}
          </TabButton>
          <TabButton
            active={tab === 'customer'}
            onClick={() => switchTab('customer')}
            testId="rules-tab-customer"
          >
            {t('rules.tab.customer')}
          </TabButton>
        </div>

        <div
          className={cn(
            'grid max-w-5xl gap-3',
            tab === 'customer' ? 'sm:grid-cols-2' : 'sm:grid-cols-1',
          )}
        >
          <CustomerLookupField
            label={t('rules.tenantLabel')}
            placeholder={t('rules.tenantPlaceholder')}
            idleHint={t('rules.tenantMinSearch')}
            loadingText={t('rules.tenantLoading')}
            emptyText={t('rules.tenantEmpty')}
            selectedPrefix={t('common.selected')}
            clearAriaLabel={`${t('rules.tenantLabel')} ${t('common.clear')}`}
            selectedCustomerId={customerId}
            selectedCustomerName={customerName}
            onSelect={setCustomerFilter}
            inputTestId="rules-customer-filter"
            resultsTestId="rules-customer-filter-results"
          />
          {tab === 'customer' ? (
            <SupplierLookupField
              allowGlobalSearch
              customerId={customerId ?? null}
              label={t('rules.supplierSearchLabel')}
              placeholder={t('ruleDialog.supplierPlaceholder')}
              idleHint={t('rules.supplierIdleHint')}
              loadingText={t('ruleDialog.supplierLoading')}
              emptyText={t('ruleDialog.supplierEmpty')}
              selectedPrefix={t('common.selected')}
              clearAriaLabel={`${t('rules.supplierSearchLabel')} ${t('common.clear')}`}
              selectedSupplierName={supplierName ?? null}
              onSelect={setSupplierFilter}
              inputTestId="rules-supplier-filter"
              resultsTestId="rules-supplier-filter-results"
            />
          ) : null}
        </div>
      </div>

      <RulesTableCard
        items={items}
        isPending={rulesQuery.isPending || editLoading}
        isError={rulesQuery.isError}
        isUnauthorized={isUnauthorized}
        errorMessage={rulesQuery.error?.message ?? ''}
        testRunPendingId={testRunLoadingId}
        togglePendingId={togglePendingId}
        deletePendingId={deletePendingId}
        onEdit={(row) => {
          void handleEdit(row);
        }}
        onTestRun={(row) => {
          void handleTestRun(row);
        }}
        onToggleActive={(row) => {
          void handleToggleActive(row);
        }}
        onDelete={(row) => {
          void handleDelete(row);
        }}
      />

      <RulesDialogs
        createOpen={createOpen}
        onCreateOpenChange={setCreateOpen}
        editTarget={editTarget}
        onClearEdit={() => setEditTarget(null)}
        testRunDraft={testRunDraft}
        onClearTestRun={() => setTestRunDraft(null)}
        deleteTarget={deleteTarget}
        onDeleteTargetChange={setDeleteTarget}
        onConfirmDelete={async (row) => {
          await deleteMutation.mutateAsync(row.id);
          setDeleteTarget(null);
        }}
        deletePending={deleteMutation.isPending}
        defaultScope={tab}
        defaultCustomerId={customerId ?? null}
        defaultCustomerName={customerName ?? null}
      />
    </section>
  );
}

interface RulesDialogsProps {
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
  editTarget: ExistingRule | null;
  onClearEdit: () => void;
  testRunDraft: RuleDraft | null;
  onClearTestRun: () => void;
  deleteTarget: RuleListItem | null;
  onDeleteTargetChange: (target: RuleListItem | null) => void;
  onConfirmDelete: (row: RuleListItem) => Promise<void>;
  deletePending: boolean;
  defaultScope: RuleScope;
  defaultCustomerId: string | null;
  defaultCustomerName: string | null;
}

function RulesDialogs({
  createOpen,
  onCreateOpenChange,
  editTarget,
  onClearEdit,
  testRunDraft,
  onClearTestRun,
  deleteTarget,
  onDeleteTargetChange,
  onConfirmDelete,
  deletePending,
  defaultScope,
  defaultCustomerId,
  defaultCustomerName,
}: RulesDialogsProps) {
  const { t } = useI18n();
  return (
    <>
      {createOpen ? (
        <RuleEditDialog
          open={createOpen}
          onOpenChange={onCreateOpenChange}
          mode="create"
          defaultScope={defaultScope}
          defaultCustomerId={defaultCustomerId}
          defaultCustomerName={defaultCustomerName}
        />
      ) : null}

      {editTarget ? (
        <RuleEditDialog
          open={!!editTarget}
          onOpenChange={(open) => {
            if (!open) onClearEdit();
          }}
          mode="edit"
          rule={editTarget}
        />
      ) : null}

      {testRunDraft ? (
        <RuleTestRunDialog
          open={!!testRunDraft}
          onOpenChange={(open) => {
            if (!open) onClearTestRun();
          }}
          draft={testRunDraft}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) onDeleteTargetChange(null);
        }}
        title={t('ruleDialog.deleteTitle')}
        description={
          deleteTarget
            ? t('rules.deleteConfirm', { title: deleteTarget.title })
            : t('ruleDialog.deleteDescription')
        }
        confirmLabel={t('ruleDialog.delete')}
        cancelLabel={t('ruleDialog.cancel')}
        confirmVariant="destructive"
        pending={deletePending}
        onConfirm={() => {
          if (!deleteTarget) return;
          void onConfirmDelete(deleteTarget).catch(() => undefined);
        }}
      />
    </>
  );
}

interface RulesTableCardProps {
  items: RuleListItem[];
  isPending: boolean;
  isError: boolean;
  isUnauthorized: boolean;
  errorMessage: string;
  togglePendingId: string | null;
  deletePendingId: string | null;
  onEdit: (row: RuleListItem) => void;
  onTestRun: (row: RuleListItem) => void;
  onToggleActive: (row: RuleListItem) => void;
  onDelete: (row: RuleListItem) => void;
  testRunPendingId: string | null;
}

function RulesTableCard({
  items,
  isPending,
  isError,
  isUnauthorized,
  errorMessage,
  testRunPendingId,
  togglePendingId,
  deletePendingId,
  onEdit,
  onTestRun,
  onToggleActive,
  onDelete,
}: RulesTableCardProps) {
  const { t } = useI18n();

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>
          {isPending ? t('rules.loading') : t('rules.count', { count: items.length })}
        </CardTitle>
        {isError ? <Badge variant="destructive">{t('rules.loadError')}</Badge> : null}
      </CardHeader>
      <CardContent className="p-0">
        {isUnauthorized ? (
          <AuthRequiredState message={errorMessage} />
        ) : isError ? (
          <div className="p-6 text-sm text-destructive" role="alert">
            {errorMessage}
          </div>
        ) : (
          <RulesTable
            items={items}
            isLoading={isPending}
            onEdit={onEdit}
            onTestRun={onTestRun}
            onToggleActive={onToggleActive}
            onDelete={onDelete}
            testRunPendingId={testRunPendingId}
            togglePendingId={togglePendingId}
            deletePendingId={deletePendingId}
          />
        )}
      </CardContent>
    </Card>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: ReactNode;
}

function TabButton({ active, onClick, testId, children }: TabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
          : 'text-muted-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}
